import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/useAuthStore';
import { captureCurrentTouch } from '../utils/attribution';
import { saveAttribution } from '../api/attribution';

/**
 * Invisible component — mounts once at the App root.
 *
 * 1. On mount: captures attribution from URL params + document.referrer,
 *    stores in localStorage (last-non-direct wins).
 * 2. When the auth token appears (sign-in or session restore): POSTs the
 *    stored attribution to /api/attribution. We only send once per token,
 *    tracked by a ref + sessionStorage to survive React StrictMode double
 *    mounting.
 */
export default function LeadSourceTracker() {
  const token = useAuthStore((s) => s.token);
  const sentRef = useRef<string | null>(null);

  useEffect(() => {
    captureCurrentTouch();
  }, []);

  useEffect(() => {
    if (!token) return;
    const sessionKey = 'lead_attribution_sent';
    const alreadySent = sessionStorage.getItem(sessionKey);
    if (alreadySent === token || sentRef.current === token) return;

    const payload = captureCurrentTouch();
    if (!payload) return;

    sentRef.current = token;
    sessionStorage.setItem(sessionKey, token);
    saveAttribution(payload).catch((e: unknown) => {
      // Soft-fail: attribution is best-effort, never block auth on it.
      // eslint-disable-next-line no-console
      console.warn('[attribution] save failed', e);
      sessionStorage.removeItem(sessionKey);
      sentRef.current = null;
    });
  }, [token]);

  return null;
}
